import type { SectorPack } from '../schema.js'

/**
 * Sector Pack "Bau & Immobilien" (M4, EDIH-Vertrag §4): generische
 * Terminologie und Regeln fuer Bau-/Projekt-Arbeitskontexte (Submission,
 * Nachtraege, Bauleitung). Keine echten Projektdaten, keine Norm-Zitate —
 * Begriffe beschreiben Arbeitspraxis, keine Rechts- oder Normbehauptungen.
 */
export const constructionPack: SectorPack = {
  id: 'construction',
  version: '1.0.0',
  label: 'Bau & Immobilien',
  terminology: {
    'Baubewilligung': 'Baubewilligung (Baugesuch)',
    'Submission': 'Submission (Ausschreibung)',
    'Nachtrag': 'Nachtrag (Mehrkostenmeldung)',
    'Devisierung': 'Devisierung (Mengenermittlung)',
    'Bauleitung': 'Bauleitung',
    'Werkvertrag': 'Werkvertrag',
    'Mängelrüge': 'Mängelrüge',
    'Abnahme': 'Bauabnahme',
    'Leistungsverzeichnis': 'Leistungsverzeichnis (LV)',
    'Kostenvoranschlag': 'Kostenvoranschlag (KV)',
    'Bauphysik': 'Bauphysik',
    'Statik': 'Statik (Tragwerksplanung)',
    'Generalunternehmer': 'Generalunternehmer (GU)',
    'Subunternehmer': 'Subunternehmer (SU)',
    'Terminplan': 'Terminplan (Bauablauf)',
    'Mängelbehebung': 'Mängelbehebung',
    'Garantie': 'Garantiefrist',
    'Baunebenkosten': 'Baunebenkosten',
    'Baukostenindex': 'Baukostenindex',
    'Ausführungsplanung': 'Ausführungsplanung',
  },
  taxonomy: {
    topics: [
      'Submission & Offerten',
      'Projektsteuerung',
      'Nachtragsmanagement',
      'Qualität & Abnahme',
      'Terminplanung',
      'Kostenkontrolle',
    ],
    documentTypes: [
      'Submissionsunterlage',
      'Offerte',
      'Nachtragsdossier',
      'Bauprotokoll',
      'Terminplan',
      'Abnahmeprotokoll',
    ],
  },
  sourceTypes: ['markdown', 'pdf', 'docx', 'email', 'web'],
  connectors: ['imap', 'rss', 'web'],
  approvedSkills: ['research', 'memory', 'blog', 'newsletter', 'presentation', 'image', 'data-analysis'],
  templates: [
    { outputType: 'angebot', templateRef: 'construction/tender-briefing' },
    { outputType: 'strategiedokument', templateRef: 'construction/nachtrag-briefing' },
    { outputType: 'praesentation', templateRef: 'construction/projekt-update' },
  ],
  providerRules: { euOnly: true, allowedProviders: ['mistral', 'anthropic'] },
  // Antwort-Waechter (RNA M4): im Bau-Kontext haftet der Gutachter fuer jede
  // Zahl und jedes Zitat. Darum laufen alle drei Waechter zwischen Retrieval
  // und Antwortausgabe. Die 8-Gramm-Breite kommt aus dem M4-Auftrag.
  answerGuards: {
    numbers: true,
    verbatim: { enabled: true, ngramSize: 8 },
    normGeneration: true,
  },
  // RNA M7 (EX_ Regel 7): ein Gutachten geht durch `output_approvals`, bevor
  // es das Haus verlaesst. Die Vorlagen halten Abschnitte ohne Beleg leer —
  // wer den Bericht verschickt, hat sie vorher gesehen.
  requiredApprovals: ['angebot', 'strategiedokument', 'praesentation', 'gutachten'],
  retentionDefaults: { chatDays: 730, auditDays: 3650 },
  disclaimers: [
    {
      outputType: 'angebot',
      text: 'Dieses Dokument wurde maschinell erstellt und ist unverbindlich. Es ersetzt keine Statik-, Bauphysik- oder Fachplanung. Mengen, Ansätze und Preise sind Richtwerte und vor einer Submission verbindlich zu prüfen.',
    },
    {
      outputType: 'strategiedokument',
      text: 'Dieses Dokument wurde maschinell erstellt und ist unverbindlich. Es ersetzt keine Statik-, Bauphysik- oder Fachplanung. Mengen, Ansätze und Preise sind Richtwerte und vor einer Verwendung verbindlich zu prüfen.',
    },
    {
      outputType: 'praesentation',
      text: 'Diese Präsentation wurde maschinell erstellt und ist unverbindlich. Sie ersetzt keine Statik-, Bauphysik- oder Fachplanung. Mengen, Ansätze und Preise sind Richtwerte und vor einer Verwendung verbindlich zu prüfen.',
    },
    {
      outputType: 'blog_artikel',
      text: 'Dieser Artikel wurde maschinell erstellt und ist unverbindlich. Er ersetzt keine Statik-, Bauphysik- oder Fachplanung. Inhalte sind vor einer Publikation durch eine qualifizierte Fachperson zu prüfen.',
    },
  ],
  goldenFixtures: [
    {
      scenario: 'projektdokumente-tender-change-briefing',
      fixtureRefs: ['construction/projektdoku-musterbau-ag.md'],
      expect: 'Tender-/Change-Briefing mit Zitaten auf die Projektdokumente und Disclaimer.',
    },
  ],
}
